import React, { useState } from "react";
import { ShieldAlert, Copy, Check, RefreshCw, Mail, X } from "lucide-react";

export interface NetworkInfo {
  is_vpn?: boolean;
  ip?: string;
  provider?: string;
  region?: string;
}

interface VpnDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  networkInfo?: NetworkInfo | null;
  userEmail?: string;
  onRecheck?: () => Promise<any>;
  rechecking?: boolean;
}

export const VpnDetailsModal: React.FC<VpnDetailsModalProps> = ({
  isOpen,
  onClose,
  networkInfo,
  userEmail,
  onRecheck,
  rechecking = false,
}) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const clientIp = networkInfo?.ip || "Detected via Proxy";
  const provider = networkInfo?.provider || "Commercial VPN / Datacenter Node";
  const region = networkInfo?.region || "Anonymized / Proxy Network";
  const rawDeviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";
  const deviceTimezone = rawDeviceTz === "Asia/Calcutta" ? "Asia/Kolkata" : rawDeviceTz;

  const handleCopyDiagnostics = () => {
    const diagnosticText = [
      "=== TechLadder Network Diagnostics ===",
      `Timestamp: ${new Date().toISOString()}`,
      `User Account: ${userEmail || "Anonymous / Current User"}`,
      `Client IP: ${clientIp}`,
      `Network Provider: ${provider}`,
      `Detected Region: ${region}`,
      `Device OS Timezone: ${deviceTimezone}`,
      `VPN Active: Yes (Read-Only Protection Triggered)`,
      "========================================",
    ].join("\n");

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(diagnosticText).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }).catch(() => {});
    }
  };

  const mailtoUrl = `mailto:support@techladder.ai?subject=${encodeURIComponent(
    `VPN Exemption Request - ${userEmail || clientIp}`
  )}&body=${encodeURIComponent(
    `Hi TechLadder Support,\n\nI am connected to our organizational VPN/network and need to request an exemption or verify our IP for write permissions on the dashboard.\n\nDiagnostics:\nIP: ${clientIp}\nProvider: ${provider}\nRegion: ${region}\nTimezone: ${deviceTimezone}\n`
  )}`;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vpn-details-title"
    >
      <div className="w-full max-w-lg rounded-2xl border border-amber-200 bg-white p-6 shadow-2xl dark:border-amber-900/60 dark:bg-slate-900 animate-in zoom-in-95 duration-200">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-4 dark:border-slate-800">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400">
              <ShieldAlert className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 id="vpn-details-title" className="text-base font-bold text-slate-900 dark:text-white">
                  Connection Diagnostics
                </h2>
                <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-950/80 dark:text-amber-300">
                  Read-Only Mode
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Network routing and security policy details
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <p className="text-xs leading-relaxed text-slate-600 dark:text-slate-300">
            TechLadder has detected that your connection is routed through a commercial VPN, datacenter proxy, or anonymized tunnel. To prevent unauthorized automated modifications and protect account integrity, your account is placed in <strong>Read-Only Mode</strong>.
          </p>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5 text-xs font-mono dark:border-slate-800 dark:bg-slate-950/50">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-slate-700 dark:text-slate-300">
              <div>
                <span className="block text-[10px] uppercase font-semibold text-slate-400 dark:text-slate-500">Public IP Address</span>
                <span className="font-semibold text-slate-900 dark:text-slate-100">{clientIp}</span>
              </div>
              <div>
                <span className="block text-[10px] uppercase font-semibold text-slate-400 dark:text-slate-500">Device Clock Timezone</span>
                <span className="font-semibold text-slate-900 dark:text-slate-100">{deviceTimezone}</span>
              </div>
              <div className="sm:col-span-2">
                <span className="block text-[10px] uppercase font-semibold text-slate-400 dark:text-slate-500">Network Provider / ASN</span>
                <span className="font-semibold text-slate-900 dark:text-slate-100">{provider}</span>
              </div>
              <div className="sm:col-span-2">
                <span className="block text-[10px] uppercase font-semibold text-slate-400 dark:text-slate-500">Detected Route / Region</span>
                <span className="font-semibold text-slate-900 dark:text-slate-100">{region}</span>
              </div>
            </div>
          </div>

          <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
            <span className="font-semibold">What can you do?</span>
            <ul className="mt-1 list-disc pl-4 space-y-0.5 text-[11px]">
              <li>You can freely view and inspect all pages, analytics, logs, and timezone settings.</li>
              <li>To save changes, update profile, or launch calls: disconnect from your VPN and click <strong>Re-check Connection</strong>.</li>
              <li>If this is an authorized company VPN, share these diagnostics with your TechLadder Admin.</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-2.5 border-t border-slate-100 pt-4 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleCopyDiagnostics}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                  <span className="text-emerald-700 dark:text-emerald-300">Copied Diagnostics</span>
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  <span>Copy for Admin</span>
                </>
              )}
            </button>
            <a
              href={mailtoUrl}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <Mail className="h-3.5 w-3.5" />
              <span>Contact Admin</span>
            </a>
          </div>

          <div className="flex items-center gap-2">
            {onRecheck && (
              <button
                type="button"
                disabled={rechecking}
                onClick={onRecheck}
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-amber-700 disabled:opacity-60"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${rechecking ? "animate-spin" : ""}`} />
                <span>{rechecking ? "Checking..." : "Re-check Connection"}</span>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VpnDetailsModal;
